import { useCallback } from "react";
import type { MessageSchema } from "../hooks/useGuidedChat";
import { useStreamingResponse } from "../hooks/useStreamingResponse";
import { MessageList } from "./MessageList";
import { ChatInput } from "./ChatInput";

interface ChatContainerProps {
  chatId: number;
  messages: MessageSchema[];
  onMessagesUpdate: (messages: MessageSchema[]) => void;
  isCompleted?: boolean;
  initialQuestion?: string;
  showFinish?: boolean;
  onFinish?: () => void;
  isFinishing?: boolean;
}

export function ChatContainer({
  chatId,
  messages,
  onMessagesUpdate,
  isCompleted = false,
  initialQuestion,
  showFinish = false,
  onFinish,
  isFinishing = false,
}: ChatContainerProps) {
  const { streamedContent, isStreaming, sendMessage, reset } =
    useStreamingResponse(chatId);

  const handleSend = useCallback(
    async (content: string) => {
      // Add user message optimistically
      const userMessage: MessageSchema = {
        id: -Date.now(),
        role: "user",
        content,
        created_at: new Date().toISOString(),
      };
      const updatedMessages = [...messages, userMessage];
      onMessagesUpdate(updatedMessages);

      // Send and stream response
      const response = await sendMessage(content);

      // Add assistant message
      if (response) {
        const assistantMessage: MessageSchema = {
          id: -Date.now(),
          role: "assistant",
          content: response,
          created_at: new Date().toISOString(),
        };
        onMessagesUpdate([...updatedMessages, assistantMessage]);
      }

      reset();
    },
    [messages, onMessagesUpdate, sendMessage, reset]
  );

  return (
    <div className="flex flex-col h-full">
      <MessageList
        messages={messages}
        streamingContent={streamedContent}
        isStreaming={isStreaming}
        initialQuestion={initialQuestion}
      />
      <ChatInput
        onSend={handleSend}
        disabled={isStreaming || isCompleted}
        placeholder={
          isCompleted
            ? "This reflection is complete"
            : "Start writing or finish your reflection..."
        }
        showFinish={showFinish}
        onFinish={onFinish}
        isFinishing={isFinishing}
      />
    </div>
  );
}
